import { useState } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  AlertCircle, 
  CheckCircle2, 
  XCircle,
  Loader2,
  AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

export default function BulkUploadQuotationsModal({ open, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error("Please select an Excel file (.xlsx or .xls)");
        return;
      }
      setFile(selectedFile);
      setUploadResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API}/quotations/bulk-upload/template`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'quotation_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Template downloaded successfully");
    } catch (error) {
      const errorMessage = error.response?.status === 403 
        ? "Only admins can download the template" 
        : "Error downloading template";
      toast.error(errorMessage);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/quotations/bulk-upload?overwrite=${overwriteExisting}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          }
        }
      );

      setUploadResult(response.data);
      
      if (response.data.success > 0) {
        toast.success(`Successfully uploaded ${response.data.success} quotation(s)`);
        if (onSuccess) {
          onSuccess();
        }
      }
      
      if (response.data.failed > 0 || response.data.skipped > 0) {
        toast.warning(`${response.data.failed} failed, ${response.data.skipped} skipped`);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Error uploading quotations";
      toast.error(errorMessage);
      setUploadResult({
        success: 0,
        failed: 1,
        skipped: 0,
        errors: [{ quotation_id: "Upload", error: errorMessage }],
        created_quotation_ids: []
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    setOverwriteExisting(false);
    onClose();
  };

  const renderErrors = () => {
    if (!uploadResult?.errors?.length) return null;

    const warnings = uploadResult.errors.filter(e => e.type === 'warning');
    const errors = uploadResult.errors.filter(e => e.type !== 'warning');

    return (
      <div className="mt-4 max-h-48 overflow-y-auto space-y-2">
        {errors.length > 0 && (
          <div className="space-y-1">
            <p className="text-sm font-medium text-destructive flex items-center gap-1">
              <XCircle className="h-4 w-4" /> Errors ({errors.length})
            </p>
            {errors.map((err, idx) => (
              <div key={`error-${err.quotation_id}-${idx}`} className="text-xs bg-destructive/10 text-destructive p-2 rounded">
                <span className="font-medium">{err.quotation_id}:</span> {err.error}
                {err.rows && <span className="text-muted-foreground ml-1">(Rows: {err.rows.join(', ')})</span>}
              </div>
            ))}
          </div>
        )}
        
        {warnings.length > 0 && (
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-600 flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" /> Warnings ({warnings.length})
            </p>
            {warnings.map((warn, idx) => (
              <div key={`warning-${warn.quotation_id}-${idx}`} className="text-xs bg-amber-50 text-amber-700 p-2 rounded">
                <span className="font-medium">{warn.quotation_id}:</span> {warn.error}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-accent" />
            Bulk Upload Quotations
          </DialogTitle>
          <DialogDescription>
            Upload historical quotation data using the Excel template format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Template Download Section */}
          <div className="bg-slate-50 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Download Template First</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Use the template to ensure your data is in the correct format. 
                  The template includes instructions and sample data.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                  data-testid="download-quotation-template-btn"
                >
                  {downloadingTemplate ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Download Template
                </Button>
              </div>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="space-y-2">
            <Label htmlFor="quotation-file">Select Excel File</Label>
            <div className="flex gap-2">
              <Input
                id="quotation-file"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="flex-1"
                data-testid="quotation-file-input"
              />
            </div>
            {file && (
              <p className="text-xs text-muted-foreground">
                Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          {/* Overwrite Option */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="overwrite"
              checked={overwriteExisting}
              onCheckedChange={setOverwriteExisting}
              data-testid="overwrite-checkbox"
            />
            <Label htmlFor="overwrite" className="text-sm cursor-pointer">
              Overwrite existing quotations with same Quotation ID
            </Label>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1 text-success">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{uploadResult.success} Success</span>
                </div>
                <div className="flex items-center gap-1 text-destructive">
                  <XCircle className="h-4 w-4" />
                  <span>{uploadResult.failed} Failed</span>
                </div>
                <div className="flex items-center gap-1 text-amber-600">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{uploadResult.skipped} Skipped</span>
                </div>
              </div>
              
              {uploadResult.created_quotation_ids?.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Created: {uploadResult.created_quotation_ids.slice(0, 5).join(', ')}
                  {uploadResult.created_quotation_ids.length > 5 && ` and ${uploadResult.created_quotation_ids.length - 5} more...`}
                </p>
              )}

              {renderErrors()}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} data-testid="cancel-bulk-upload-btn">
            {uploadResult ? 'Close' : 'Cancel'}
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            data-testid="upload-quotations-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload Quotations
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
